import type { CSSProperties, ReactNode } from 'react';

import styles from './ShimmerCard.module.css';

/**
 * Перенос `lib/src/presentation/widgets/shimmer_card.dart`.
 *
 * Параметры `direction` и `loop` из Dart-версии не переносились: во всех
 * вызовах они остаются умолчаниями (слева направо, бесконечно), и оба
 * зашиты в CSS-модуль.
 */

/** `period: const Duration(milliseconds: 1500)` */
const SHIMMER_PERIOD_MS = 1500;

export interface ShimmerCardProps {
  readonly enabled?: boolean;
  /** Число — пиксели, строка — готовое CSS-значение. */
  readonly borderRadius?: number | string;
  readonly height?: number | string;
  readonly width?: number | string;
  readonly children?: ReactNode;
  readonly expand?: boolean;
  /** Длительность одного прохода блика, мс. */
  readonly period?: number;
}

function cssSize(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

interface ShimmerPlaceholderProps {
  readonly expand: boolean;
  readonly height: number | string | undefined;
  readonly width: number | string | undefined;
}

function ShimmerPlaceholder({ expand, height, width }: ShimmerPlaceholderProps) {
  const style: CSSProperties = {
    height: expand ? '100%' : cssSize(height),
    width: expand ? '100%' : cssSize(width),
  };
  return <div className={styles.box} style={style} />;
}

export function ShimmerCard({
  enabled = true,
  borderRadius,
  height,
  width,
  children,
  expand = false,
  period = SHIMMER_PERIOD_MS,
}: ShimmerCardProps) {
  // Dart: `if (!enabled) return child ?? SizedBox();`
  if (!enabled) return <>{children}</>;

  const radius = cssSize(borderRadius);
  // `buildWithBorderRadius`: ClipRRect появляется только с заданным радиусом.
  const style = {
    '--shimmer-period': `${period}ms`,
    ...(radius === undefined
      ? {}
      : { borderRadius: radius, overflow: 'hidden' }),
  } as CSSProperties;

  return (
    <div
      className={`${styles.shimmer} ${expand ? styles.expand : ''}`}
      style={style}
    >
      {children ?? (
        <ShimmerPlaceholder expand={expand} height={height} width={width} />
      )}
    </div>
  );
}
